import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../i18n/LanguageContext';
import { EDITABLE_SLIDES, getPath, setPath } from '../admin/editableSlides';
import AppRebuildCard from '../components/AppRebuildCard';
import PdfShareCard from '../components/PdfShareCard';
import { translations } from '../i18n/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DE = translations.de;

const inputCls = 'w-full bg-white/[0.04] border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30 font-ui';
const labelCls = 'block text-[11px] uppercase tracking-[0.18em] text-white/45 mb-1 font-ui';

function TextField({ label, value, multiline, onChange, testId }) {
  return (
    <div className="mb-3">
      <label className={labelCls}>{label}</label>
      {multiline ? (
        <textarea data-testid={testId} className={`${inputCls} min-h-[70px]`} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testId} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function ImageUpload({ label, url, onUploaded, testId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload`, fd);
      onUploaded(data.url);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="w-14 h-14 rounded bg-white/[0.05] border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
        {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <span className="text-white/25 text-xs">—</span>}
      </div>
      <div className="min-w-0">
        <div className="text-xs text-white/60 font-ui mb-1 truncate">{label}</div>
        <button
          type="button"
          data-testid={testId}
          onClick={() => fileRef.current?.click()}
          className="text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded border border-white/15 text-white/80 hover:border-white/40 transition-colors"
          disabled={busy}
        >
          {busy ? 'Lädt…' : 'Bild hochladen'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const { refreshContent } = useT();
  const [activeKey, setActiveKey] = useState(EDITABLE_SLIDES[0].key);
  const [draft, setDraft] = useState(null);       // { overrides, styles, images }
  const [status, setStatus] = useState('');
  const dragIndex = useRef(null);

  const slide = useMemo(() => EDITABLE_SLIDES.find((s) => s.key === activeKey), [activeKey]);
  const base = DE[activeKey] || {};

  const loadSlide = async (key) => {
    setStatus('');
    try {
      const { data } = await axios.get(`${API}/content`);
      const entry = data[key] || {};
      setDraft({
        overrides: entry.overrides || {},
        styles: entry.styles || {},
        images: entry.images || {},
      });
    } catch (_) {
      setDraft({ overrides: {}, styles: {}, images: {} });
    }
  };

  useEffect(() => { loadSlide(activeKey); }, [activeKey]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0e13] text-white font-ui" data-testid="admin-denied">
        <div className="text-center">
          <div className="text-lg mb-2">Kein Zugriff</div>
          <div className="text-white/50 text-sm mb-4">Dieses Dashboard ist Admins vorbehalten.</div>
          <Link to="/dashboard" className="underline text-white/70">Zum Dashboard</Link>
        </div>
      </div>
    );
  }
  if (!draft) return <div className="min-h-screen bg-[#0b0e13]" />;

  const effVal = (path) => {
    const ov = getPath(draft.overrides, path);
    return ov != null && ov !== '' ? ov : (getPath(base, path) ?? '');
  };
  const setField = (path, v) => setDraft((d) => ({ ...d, overrides: setPath(d.overrides, path, v) }));

  // --- List handling (arrays replace base on save) ---
  const listCfg = slide.list;
  const listItems = listCfg
    ? (getPath(draft.overrides, listCfg.path) || (getPath(base, listCfg.path) || []).map((it, i) => ({
        ...it,
        ...(listCfg.imageField ? { [listCfg.imageField]: it[listCfg.imageField] || listCfg.imageDefault(i) } : {}),
      })))
    : [];
  const setListItems = (items) => setDraft((d) => ({ ...d, overrides: setPath(d.overrides, listCfg.path, items) }));

  const onDrop = (i) => {
    const from = dragIndex.current;
    if (from == null || from === i) return;
    const items = [...listItems];
    const [moved] = items.splice(from, 1);
    items.splice(i, 0, moved);
    setListItems(items);
    dragIndex.current = null;
  };

  const save = async () => {
    setStatus('speichert…');
    try {
      await axios.put(`${API}/admin/content/${activeKey}`, draft);
      refreshContent();
      setStatus('Gespeichert ✓');
      setTimeout(() => setStatus(''), 2500);
    } catch (e) {
      setStatus('Fehler beim Speichern');
    }
  };

  const reset = async () => {
    if (!window.confirm('Alle Änderungen dieser Folie auf Original zurücksetzen?')) return;
    await axios.delete(`${API}/admin/content/${activeKey}`);
    refreshContent();
    loadSlide(activeKey);
  };

  return (
    <div className="min-h-screen bg-[#0b0e13] text-white font-ui" data-testid="admin-page">
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">STIG ROCK · Admin</div>
          <h1 className="font-display text-xl tracking-tight">Folien-Inhalte bearbeiten <span className="text-white/40 text-sm">(gilt für Deutsch)</span></h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-xs uppercase tracking-[0.14em] text-white/60 hover:text-white" data-testid="admin-to-dashboard">Dashboard</Link>
          <Link to={`/present#${slide.anchor}`} className="text-xs uppercase tracking-[0.14em] px-3 py-1.5 rounded border border-white/20 hover:border-white/50" data-testid="admin-preview-link">Folie ansehen →</Link>
        </div>
      </header>

      <div className="flex">
        <aside className="w-64 shrink-0 border-r border-white/10 min-h-[calc(100vh-73px)] p-3">
          {EDITABLE_SLIDES.map((s) => (
            <button
              key={s.key}
              data-testid={`admin-slide-${s.key}`}
              onClick={() => setActiveKey(s.key)}
              className={`w-full text-left px-3 py-2.5 rounded mb-1 text-sm transition-colors ${
                s.key === activeKey ? 'bg-white/10 text-white' : 'text-white/55 hover:bg-white/[0.05]'
              }`}
            >
              {s.label}
            </button>
          ))}
          <AppRebuildCard />
          <PdfShareCard />
        </aside>

        <main className="flex-1 p-6 max-w-[860px]">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-lg">{slide.label}</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/50" data-testid="admin-status">{status}</span>
              <button onClick={reset} data-testid="admin-reset-btn" className="text-xs uppercase tracking-[0.14em] px-3 py-2 rounded border border-white/15 text-white/60 hover:border-red-400/60 hover:text-red-300">Zurücksetzen</button>
              <button onClick={save} data-testid="admin-save-btn" className="text-xs uppercase tracking-[0.14em] px-5 py-2 rounded bg-gradient-to-b from-[#f2d979] to-[#c9a13d] text-[#1a1408] font-semibold hover:brightness-105">Speichern</button>
            </div>
          </div>

          {/* Text fields */}
          <section className="mb-6">
            {slide.fields.map((f) => (
              <TextField
                key={f.path}
                label={f.label}
                multiline={f.multiline}
                value={effVal(f.path)}
                onChange={(v) => setField(f.path, v)}
                testId={`admin-field-${slide.key}-${f.path.replace(/\./g, '-')}`}
              />
            ))}
          </section>

          {/* Font sizes */}
          <section className="mb-6">
            <div className={labelCls}>Schriftgrößen (px — leer = Standard)</div>
            <div className="flex gap-4">
              {['overline', 'title', 'body'].map((k) => (
                <div key={k}>
                  <label className="block text-[10px] text-white/40 mb-1 uppercase">{k === 'overline' ? 'Overline' : k === 'title' ? 'Titel' : 'Fließtext'}</label>
                  <input
                    type="number"
                    min="8"
                    max="200"
                    data-testid={`admin-fontsize-${slide.key}-${k}`}
                    className={`${inputCls} w-24`}
                    value={draft.styles[k] || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, styles: { ...d.styles, [k]: e.target.value ? Number(e.target.value) : undefined } }))}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Image slots */}
          {slide.imageSlots && (
            <section className="mb-6">
              <div className={labelCls}>Bilder</div>
              {slide.imageSlots.map((s) => (
                <ImageUpload
                  key={s.key}
                  label={s.label}
                  url={draft.images[s.key]}
                  testId={`admin-image-${slide.key}-${s.key}`}
                  onUploaded={(url) => setDraft((d) => ({ ...d, images: { ...d.images, [s.key]: url } }))}
                />
              ))}
            </section>
          )}

          {/* List editor with drag & drop */}
          {listCfg && (
            <section className="mb-8">
              <div className={labelCls}>{listCfg.label}</div>
              {listItems.map((item, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={() => { dragIndex.current = i; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  data-testid={`admin-list-item-${i}`}
                  className="border border-white/10 rounded p-3 mb-2 bg-white/[0.02] cursor-grab active:cursor-grabbing"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/30 text-xs select-none">⠿ Position {i + 1}</span>
                    {listCfg.imageField && (
                      <ImageUpload
                        label="Bild"
                        url={item[listCfg.imageField]}
                        testId={`admin-list-image-${i}`}
                        onUploaded={(url) => {
                          const items = [...listItems];
                          items[i] = { ...items[i], [listCfg.imageField]: url };
                          setListItems(items);
                        }}
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {listCfg.itemFields.map((f) => (
                      <div key={f.path} className={f.multiline ? 'col-span-2' : ''}>
                        <TextField
                          label={f.label}
                          multiline={f.multiline}
                          value={item[f.path] ?? ''}
                          onChange={(v) => {
                            const items = [...listItems];
                            items[i] = { ...items[i], [f.path]: v };
                            setListItems(items);
                          }}
                          testId={`admin-list-${i}-${f.path}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
